(() => {
  const initialized = new WeakSet();

  function initSlideshow(root) {
    if (initialized.has(root)) return;
    initialized.add(root);

    const slides = [...root.querySelectorAll('[data-mf-slide]')];
    const dots = [...root.querySelectorAll('[data-mf-dot]')];
    const previous = root.querySelector('[data-mf-previous]');
    const next = root.querySelector('[data-mf-next]');
    if (!slides.length) return;

    let active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
    let timer;

    const show = (index) => {
      active = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const selected = slideIndex === active;
        slide.classList.toggle('is-active', selected);
        slide.toggleAttribute('hidden', !selected);
        slide.setAttribute('aria-hidden', selected ? 'false' : 'true');
      });
      dots.forEach((dot, dotIndex) => {
        const selected = dotIndex === active;
        dot.classList.toggle('is-active', selected);
        dot.setAttribute('aria-current', selected ? 'true' : 'false');
      });
    };

    const restart = () => {
      window.clearInterval(timer);
      if (root.dataset.autoplay !== 'true' || slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const delay = Math.max(3, Number(root.dataset.interval || 6)) * 1000;
      timer = window.setInterval(() => show(active + 1), delay);
    };

    previous?.addEventListener('click', () => { show(active - 1); restart(); });
    next?.addEventListener('click', () => { show(active + 1); restart(); });
    dots.forEach((dot, index) => dot.addEventListener('click', () => { show(index); restart(); }));
    root.addEventListener('mouseenter', () => window.clearInterval(timer));
    root.addEventListener('mouseleave', restart);
    root.addEventListener('focusin', () => window.clearInterval(timer));
    root.addEventListener('focusout', restart);
    show(active);
    restart();
  }

  function initCountdown(root) {
    if (initialized.has(root)) return;
    initialized.add(root);
    const mode = root.dataset.mode || 'fixed';
    const configuredEnd = Date.parse(root.dataset.end || '');
    const rawOffset = root.dataset.offset || '+0000';
    const offsetMatch = rawOffset.match(/^([+-])(\d{2}):?(\d{2})$/);
    const offsetMinutes = offsetMatch
      ? (offsetMatch[1] === '-' ? -1 : 1) * (Number(offsetMatch[2]) * 60 + Number(offsetMatch[3]))
      : 0;
    const resetHour = Math.min(23, Math.max(0, Number(root.dataset.resetHour || 0)));
    const output = {
      days: root.querySelector('[data-mf-days]'),
      hours: root.querySelector('[data-mf-hours]'),
      minutes: root.querySelector('[data-mf-minutes]'),
      seconds: root.querySelector('[data-mf-seconds]')
    };

    const nextDailyReset = (now) => {
      const shifted = new Date(now + offsetMinutes * 60000);
      let end = Date.UTC(
        shifted.getUTCFullYear(),
        shifted.getUTCMonth(),
        shifted.getUTCDate(),
        resetHour,
        0,
        0
      ) - offsetMinutes * 60000;
      if (end <= now) end += 86400000;
      return end;
    };

    if (mode !== 'daily' && !Number.isFinite(configuredEnd)) return;

    let timer;
    let end = mode === 'daily' ? nextDailyReset(Date.now()) : configuredEnd;
    const update = () => {
      if (mode === 'daily' && end <= Date.now()) end = nextDailyReset(Date.now());
      const remaining = Math.max(0, end - Date.now());
      const days = Math.floor(remaining / 86400000);
      const hours = Math.floor((remaining % 86400000) / 3600000);
      const minutes = Math.floor((remaining % 3600000) / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      if (output.days) output.days.textContent = String(days).padStart(2, '0');
      if (output.hours) output.hours.textContent = String(hours).padStart(2, '0');
      if (output.minutes) output.minutes.textContent = String(minutes).padStart(2, '0');
      if (output.seconds) output.seconds.textContent = String(seconds).padStart(2, '0');
      if (remaining === 0 && mode !== 'daily') {
        window.clearInterval(timer);
        root.closest('[data-mf-offer]')?.classList.add('is-expired');
      }
    };
    update();
    timer = window.setInterval(update, 1000);
  }

  function initCopy(button) {
    if (initialized.has(button)) return;
    initialized.add(button);
    button.addEventListener('click', async () => {
      const code = button.dataset.copy || '';
      if (!code) return;
      try {
        await navigator.clipboard.writeText(code);
      } catch (_) {
        const input = document.createElement('textarea');
        input.value = code;
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }
      const original = button.textContent;
      button.textContent = button.dataset.copiedLabel || 'Copied';
      window.setTimeout(() => { button.textContent = original; }, 1800);
    });
  }

  function initCarousel(root) {
    if (initialized.has(root)) return;
    initialized.add(root);

    const track = root.querySelector('[data-mf-carousel-track]');
    const previous = root.querySelector('[data-mf-carousel-prev]');
    const next = root.querySelector('[data-mf-carousel-next]');
    if (!track) return;

    let frame;
    const update = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
        const atStart = track.scrollLeft <= 3;
        const atEnd = track.scrollLeft >= maxScroll - 3;
        if (previous) previous.disabled = maxScroll <= 3 || atStart;
        if (next) next.disabled = maxScroll <= 3 || atEnd;
      });
    };

    const move = (direction) => {
      const firstCard = track.firstElementChild;
      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || 0);
      const cardWidth = firstCard?.getBoundingClientRect().width || track.clientWidth * 0.8;
      const cardsPerView = Math.max(1, Math.floor(track.clientWidth / Math.max(1, cardWidth + gap)));
      track.scrollBy({ left: direction * (cardWidth + gap) * cardsPerView, behavior: 'smooth' });
    };

    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
    track.addEventListener('scroll', update, { passive: true });
    root.addEventListener('mf:carousel:refresh', update);
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
    update();
  }

  function initFeaturedCollections(root) {
    if (initialized.has(root)) return;
    initialized.add(root);

    const tabs = [...root.querySelectorAll('[data-mf-collection-tab]')];
    const panels = [...root.querySelectorAll('[data-mf-collection-panel]')];
    if (!tabs.length || !panels.length) return;

    const activate = (tab, moveFocus = false) => {
      const panelId = tab.getAttribute('aria-controls');
      tabs.forEach((item) => {
        const selected = item === tab;
        item.classList.toggle('is-active', selected);
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
        item.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel) => {
        const selected = panel.id === panelId;
        panel.classList.toggle('is-active', selected);
        panel.hidden = !selected;
        if (selected) {
          window.requestAnimationFrame(() => panel.dispatchEvent(new CustomEvent('mf:carousel:refresh')));
        }
      });
      if (moveFocus) tab.focus();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        let targetIndex = index;
        if (event.key === 'ArrowRight') targetIndex = (index + 1) % tabs.length;
        else if (event.key === 'ArrowLeft') targetIndex = (index - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') targetIndex = 0;
        else if (event.key === 'End') targetIndex = tabs.length - 1;
        else return;
        event.preventDefault();
        activate(tabs[targetIndex], true);
      });
    });
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-mf-slideshow]').forEach(initSlideshow);
    scope.querySelectorAll('[data-mf-countdown]').forEach(initCountdown);
    scope.querySelectorAll('[data-mf-copy]').forEach(initCopy);
    scope.querySelectorAll('[data-mf-featured-collections]').forEach(initFeaturedCollections);
    scope.querySelectorAll('[data-mf-carousel]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
